import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pagination } from '../components/Pagination';
import { StatusBadge } from '../components/StatusBadge';
import { QUERY_PAGE_SIZE, RIDDLE_STATUSES } from '../config';
import { loadReferences } from '../lib/data';
import { errorMessage } from '../lib/errors';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/text';
import type { Category, ContentPack, Language, Riddle, RiddleStatus } from '../lib/types';

export function RiddlesPage() {
  const [rows, setRows] = useState<Riddle[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('');
  const [category, setCategory] = useState('');
  const [pack, setPack] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void loadReferences()
      .then((data) => {
        setLanguages(data.languages);
        setCategories(data.categories);
        setPacks(data.packs);
      })
      .catch((caught) => setError(errorMessage(caught)));
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const from = (page - 1) * QUERY_PAGE_SIZE;
      const to = from + QUERY_PAGE_SIZE - 1;
      let query = supabase
        .from('riddles')
        .select('*', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(from, to);

      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        const safe = trimmedSearch.replaceAll(',', ' ');
        query = query.or(
          `id.ilike.%${safe}%,title_internal.ilike.%${safe}%,answer_display.ilike.%${safe}%`,
        );
      }
      if (language) query = query.eq('language_code', language);
      if (category) query = query.eq('category_id', category);
      if (pack) query = query.eq('content_pack_id', pack);
      if (status) query = query.eq('status', status);

      const { data, error: queryError, count } = await query;
      if (queryError) throw queryError;
      setRows((data ?? []) as Riddle[]);
      setTotal(count ?? 0);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [category, language, pack, page, search, status]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadRows(), 250);
    return () => window.clearTimeout(timer);
  }, [loadRows]);

  const categoryLabels = useMemo(
    () => new Map(categories.map((item) => [item.id, item.label])),
    [categories],
  );

  const remove = async (riddle: Riddle) => {
    if (!window.confirm(`Supprimer définitivement l’énigme « ${riddle.title_internal} » ?`)) return;
    try {
      const { error: deleteError } = await supabase.from('riddles').delete().eq('id', riddle.id);
      if (deleteError) throw deleteError;
      await loadRows();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const transition = async (riddle: Riddle, nextStatus: RiddleStatus) => {
    setError('');
    try {
      const { error: updateError } = await supabase
        .from('riddles')
        .update({ status: nextStatus })
        .eq('id', riddle.id);
      if (updateError) throw updateError;
      await loadRows();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Catalogue</p>
          <h1>Énigmes</h1>
          <p>Recherche, filtre, révise et publie le contenu éditorial.</p>
        </div>
        <Link className="button primary" to="/riddles/new">Nouvelle énigme</Link>
      </header>

      <section className="panel filter-panel">
        <div className="filter-grid">
          <label className="wide-field">
            Recherche
            <input
              type="search"
              placeholder="ID, titre ou réponse…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <label>
            Langue
            <select value={language} onChange={(event) => { setLanguage(event.target.value); setPage(1); }}>
              <option value="">Toutes</option>
              {languages.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
            </select>
          </label>
          <label>
            Catégorie
            <select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }}>
              <option value="">Toutes</option>
              {categories.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label>
            Pack
            <select value={pack} onChange={(event) => { setPack(event.target.value); setPage(1); }}>
              <option value="">Tous</option>
              {packs.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>
            Statut
            <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
              <option value="">Tous</option>
              {RIDDLE_STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>
      </section>

      {error && <div className="alert error">{error}</div>}

      <section className="panel table-panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Énigme</th>
                <th>Catégorie</th>
                <th>Difficulté</th>
                <th>Statut</th>
                <th>Révision</th>
                <th>Mise à jour</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="empty-cell">Chargement…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="empty-cell">Aucune énigme ne correspond aux filtres.</td></tr>
              ) : (
                rows.map((riddle) => (
                  <tr key={riddle.id}>
                    <td>
                      <Link className="row-title" to={`/riddles/${encodeURIComponent(riddle.id)}`}>
                        {riddle.title_internal}
                      </Link>
                      <span className="row-subtitle">{riddle.id} · {riddle.answer_display}</span>
                    </td>
                    <td>{categoryLabels.get(riddle.category_id) ?? riddle.category_id}</td>
                    <td>{riddle.difficulty}/5</td>
                    <td><StatusBadge status={riddle.status} /></td>
                    <td>v{riddle.revision}</td>
                    <td>{formatDate(riddle.updated_at)}</td>
                    <td className="row-actions">
                      {riddle.status === 'draft' && <button className="button secondary small" onClick={() => void transition(riddle, 'needs_review')}>Envoyer en révision</button>}
                      {riddle.status === 'needs_review' && <button className="button secondary small" onClick={() => void transition(riddle, 'approved')}>Approuver</button>}
                      {riddle.status === 'needs_review' && <button className="button danger ghost small" onClick={() => void transition(riddle, 'declined')}>Refuser</button>}
                      {riddle.status === 'declined' && <button className="button secondary small" onClick={() => void transition(riddle, 'draft')}>Repasser en brouillon</button>}
                      <Link className="button ghost small" to={`/riddles/${encodeURIComponent(riddle.id)}`}>
                        Modifier
                      </Link>
                      <button className="button danger ghost small" onClick={() => void remove(riddle)}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={QUERY_PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
}
